import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  selectScriptCategoriesByCreator,
  selectScriptsByCreatorAndFilter,
  selectScriptsLoadingByCreatorAndFilter,
} from "../../../../redux/chatScript/selectors/scriptSelectors";
import { fetchScriptCategoriesByCreator, fetchScriptsByFilter } from "../../../../redux/chatScript/thunks/scriptThunks";
import { FILE, PENDING, REQUEST_LOADING_STATES, TEXT } from "../../../../helpers/constant";
import ScriptCategories from "../../ScriptCategories/ScriptCategories";
import ScriptList, { ScriptItem } from "../../ScriptList/ScriptList";
import WhitePlus from "../../../../assets/icons/white-plus.svg";
import WhiteSearch from "../../../../assets/icons/white-search.svg";
import Loader from "../../../loader/Loader";
import useFetchUserInfos from "../../../../hooks/userHooks/useFetchUserInfos";
import { useNavigate } from "react-router-dom";
import ScriptPickerSearch from "./ScriptPickerSearch";
import "./ScriptsPickerModal.less";
import { useListPaging } from "../../../../hooks/useListPaging";
import { addMessageInstant, sendScriptAction } from "../../../../redux/messages/messagesSlice";
import { useSelector } from "react-redux";
import { StoreState } from "../../../../redux/store";
import AXIOS_API from "../../../../helpers/axios";

export type ScriptsPickerModalProps = {
  onClose: () => void;
};

const ScriptsPickerModal: React.FC<ScriptsPickerModalProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentUser } = useFetchUserInfos();
  const currentCreator = currentUser?.id;
  const categories = useAppSelector(selectScriptCategoriesByCreator(currentCreator!));
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const scriptsEntry = useAppSelector(selectScriptsByCreatorAndFilter(currentCreator!, activeCategory));
  const scriptsLoading = useAppSelector(selectScriptsLoadingByCreatorAndFilter(currentCreator!, activeCategory));

  const { selectedConversation } = useSelector((state: StoreState) => state.messages);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const lastRequestedPageRef = useRef<number>(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [alreadySentIds, setAlreadySentIds] = useState<string[]>([]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (currentCreator && categories.length === 0) {
      dispatch(fetchScriptCategoriesByCreator(currentCreator));
    }
  }, [dispatch, currentCreator, categories.length]);

  useEffect(() => {
    if (currentCreator) {
      lastRequestedPageRef.current = scriptsEntry.page || 0;
      if (!scriptsEntry || !scriptsEntry.items || scriptsEntry.items.length === 0) {
        lastRequestedPageRef.current = 1;
        dispatch(fetchScriptsByFilter({ creatorId: currentCreator, filterOrCategoryId: activeCategory, page: 1 }));
      }
    }
  }, [dispatch, currentCreator, activeCategory]);

  useEffect(() => {
    let isMounted = true;
    async function fetchAlreadySent() {
      try {
        const convId = selectedConversation?._id;
        if (!convId) return;
        const { data } = await AXIOS_API.get(`/script-message/conversation/${convId}/scripts`, { withCredentials: true });
        const ids: string[] = Array.isArray(data?.data) ? data.data : [];
        if (isMounted) setAlreadySentIds(ids);
      } catch (e) {
        if (isMounted) setAlreadySentIds([]);
      }
    }
    fetchAlreadySent();
    return () => {
      isMounted = false;
      setAlreadySentIds([]);
    };
  }, [selectedConversation?._id]);

  const sendScript = (item: ScriptItem) => {
    const newMessage: {
      temp_id: string;
      conversationId: string;
      senderId: string;
      receiverId: string;
      status?: string;
      creatorId: string;
      type?: string;
      uploading?: boolean;
      medias?: any[];
      text: string;
      // price? : number
    } = {
      temp_id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      conversationId: selectedConversation?._id!,
      senderId: currentCreator!,
      receiverId: selectedConversation?.interlocutor?.userId!,
      creatorId: currentCreator!,
      text: item.message,
    };
    // if (item.type === "script") {
    if (item.scriptType === "FILE") {
      newMessage.type = FILE;
      newMessage.status = PENDING;
      newMessage.uploading = true;
      newMessage.medias = [];
      // newMessage.price = item.price!;
    } else {
      newMessage.type = TEXT;
    }
    if (item.message.includes("/name")) {
      const firstName = selectedConversation?.interlocutor?.name?.split(" ")[0] || "";
      newMessage.text = item.message.replace("/name", firstName);
    }
    dispatch(addMessageInstant(newMessage));
    dispatch(
      sendScriptAction({
        creatorId: currentCreator,
        fanId: selectedConversation?.interlocutor?.userId!,
        scriptId: item.id,
        conversationId: selectedConversation?._id!,
        temp_id: newMessage.temp_id,
      })
    );
    // }

    onClose();
  };

  useListPaging({
    listRef,
    sentinelRef,
    itemsLength: scriptsEntry.items?.length || 0,
    page: scriptsEntry.page || 1,
    totalPages: scriptsEntry.totalPages || 0,
    pageSize: (scriptsEntry as any)?.pageSize || 15,
    loadingState: scriptsLoading,
    lastRequestedPageRef,
    canRun: !!currentCreator,
    onLoadNext: (nextPage) => {
      if (!currentCreator) return;
      dispatch(fetchScriptsByFilter({ creatorId: currentCreator, filterOrCategoryId: activeCategory, page: nextPage }));
    },
  });

  const items: ScriptItem[] = useMemo(
    () =>
      (scriptsEntry.items || []).map((s) => ({
        id: s._id,
        title: s.title,
        message: s.message,
        price: s.price ?? 0,
        type: "script",
        category: activeCategory,
        scriptType: s.scriptType,
      })),
    [scriptsEntry.items, activeCategory]
  );

  return (
    <div className="scripts-picker-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className={`scripts-picker-modal-content${showSearch ? " search-mode" : ""}`} onClick={(e) => e.stopPropagation()} role="document">
        <div className="scripts-picker-header-line-wrapper" onClick={onClose}>
          <div className="scripts-picker-header-line"></div>
        </div>
        {!showSearch ? (
          <>
            <header className="scripts-picker-modal-header">
              <button
                type="button"
                className="scripts-picker-header-icon left"
                onClick={() => navigate(`/creator/chat-scripts/create?conversationId=${selectedConversation?._id}`)}
                aria-label="add"
              >
                <img src={WhitePlus} alt="add" />
              </button>
              <h2 className="scripts-picker-title">{t("your-scripts")}</h2>
              {categories.length > 0 && (
                <button type="button" className="scripts-picker-header-icon right" onClick={() => setShowSearch(true)} aria-label="search">
                  <img src={WhiteSearch} alt="search" />
                </button>
              )}
            </header>

            {categories.length > 0 ? (
              <div className="scripts-picker-body">
                <ScriptCategories
                  creatorId={currentCreator!}
                  categories={categories.map((c) => ({ _id: c._id, name: c.name }))}
                  activeCategory={activeCategory}
                  onSelect={setActiveCategory}
                />

                <ScriptList
                  items={items}
                  loading={scriptsLoading === REQUEST_LOADING_STATES.PENDING && items.length === 0}
                  listRef={listRef}
                  sentinelRef={sentinelRef}
                  pickerMode
                  alreadySentIds={alreadySentIds}
                  onSend={(item) => {
                    sendScript(item);
                  }}
                />

                {scriptsLoading === REQUEST_LOADING_STATES.PENDING && items.length > 0 && (
                  <div className="loader-script-wrapper">
                    <Loader />
                  </div>
                )}
              </div>
            ) : (
              <div className="no-category-card" role="note" aria-live="polite">
                {t("no-script-created")}
              </div>
            )}
          </>
        ) : (
          <ScriptPickerSearch
            allItems={items}
            onCancel={() => setShowSearch(false)}
            alreadySentIds={alreadySentIds}
            onSend={(item) => {
              sendScript(item);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ScriptsPickerModal;
